import { CAMERA_COVERAGE_PRESETS } from '@repo/front-end-utils';

/**
 * The camera panel's sentences (`camera-feeds-page-panel`), kept pure so each one is tested
 * without rendering. Every sentence the panel shows or announces is built here; the panel only
 * decides which to show.
 */

/** What a floor is called on this step: its name, or its place when it has none. */
export const floorLabel = (name: string | null | undefined, index: number): string =>
  name?.trim() || `Floor ${index + 1}`;

/**
 * The header's second line (frames 04–07). A camera placed during this visit is "New camera"
 * whatever it shows; an existing camera says which stream it shows, or that it shows none.
 */
export const cameraPanelSubtitle = ({
  isNew,
  streamName,
  floor,
}: {
  isNew: boolean;
  /** The stream's name, or null for a camera with no stream. */
  streamName: string | null;
  floor: string;
}): string => {
  if (isNew) return `New camera · ${floor}`;
  return streamName != null ? `Shows ${streamName} · ${floor}` : `No stream · ${floor}`;
};

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The one line that replaced the panel's two in-view lists (frame 05 note 6). The design draws only
 * plural examples; the singular and zero forms are this feature's, recorded for Rob in PROVENANCE.
 * A new camera adds what the count is for (frame 04).
 */
export const cameraSightSentence = ({
  areas,
  items,
  newCameraName,
}: {
  areas: number;
  items: number;
  /** Set only while the header is cyan: the camera was placed during this visit. */
  newCameraName?: string;
}): string => {
  const sees =
    areas === 0 && items === 0
      ? 'Sees no areas or equipment yet.'
      : `Sees ${count(areas, 'area', 'areas')} and ${count(items, 'piece', 'pieces')} of equipment.`;
  // "These" needs something to point at: a new camera that sees nothing yet gets no second sentence.
  return newCameraName && (areas > 0 || items > 0)
    ? `${sees} The Observers step offers these first for ${newCameraName}.`
    : sees;
};

/**
 * The Name field's helper text (frames 04, 06, 07), plus the two states the board does not draw: a
 * linked camera with no name of its own (it reads "Camera N"), and a stream whose name the read has
 * not given — the helper never claims "Same as the stream" when it does not know the stream's name.
 */
export const cameraNameHelper = ({
  hasStream,
  name,
  streamName,
  displayName,
}: {
  hasStream: boolean;
  name: string;
  /** The stream's name when the streams read has given it. */
  streamName?: string;
  /** What the camera reads as — "Camera N" when it has no name. */
  displayName?: string;
}): string => {
  if (!hasStream) return 'Takes the stream’s name when you link one.';
  if (name.trim() === '')
    return `No name yet, so it reads ${displayName ?? 'Camera N'}. Type to name it.`;
  if (streamName == null) return 'Type to rename.';
  if (name === streamName) return 'Same as the stream. Type to rename.';
  return `Renamed. Clear it to use the stream’s name, ${streamName}.`;
};

/**
 * What a camera's name becomes when its Name field is left (ruling 1, 2026-10-11): an emptied field
 * on a camera with a stream goes back to the stream's name, which is then STORED — so every reader
 * of `camera.name` (the plan label, the delete warning, plan search, the wall footer) agrees without
 * knowing about streams. A camera with no stream keeps the empty name, read as "Camera N".
 *
 * `null` means "keep the name it had": the camera has a stream whose name the streams read has not
 * given, and writing "Stream 12" into an operator's camera would be worse than not clearing.
 * Anything else is stored as typed (untrimmed, as the Floor Plan step stores it).
 */
export const nameOnLeave = ({
  value,
  hasStream,
  streamName,
}: {
  value: string;
  hasStream: boolean;
  streamName?: string;
}): string | null => {
  if (value.trim() !== '') return value;
  if (!hasStream) return '';
  return streamName ?? null;
};

/**
 * The coverage readout, the same rule the marked slider applies to its own header: the mark's name
 * and the angle on a mark ("Wide · 100°"), the angle alone between marks ("70°"), and the angle once
 * where the mark is named after it ("360°"). Built from the presets so it cannot drift from the marks.
 */
export const coverageReadout = (fovAngle: number): string => {
  const degrees = `${fovAngle}°`;
  const mark = CAMERA_COVERAGE_PRESETS.find(p => p.fovAngle === fovAngle);
  if (!mark || mark.label === degrees) return degrees;
  return `${mark.label} · ${degrees}`;
};
